
interface AnalyticsEvent {
  id: string;
  event: string;
  properties: Record<string, any>;
  timestamp: string;
  userId?: string;
  sessionId: string;
}

interface PageView {
  page: string;
  timestamp: string;
  userId?: string;
  sessionId: string;
  referrer?: string;
  duration?: number;
}

class AnalyticsService {
  private events: AnalyticsEvent[] = [];
  private pageViews: PageView[] = [];
  private sessionId: string;
  private userId?: string;
  private currentPage?: PageView;
  private maxEvents = 1000;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.initializeSession();
  }

  private initializeSession() {
    // Track session start
    this.trackEvent('session_start', {
      userAgent: navigator.userAgent,
      screen: {
        width: window.screen.width,
        height: window.screen.height
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    });

    // Track page visibility changes
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackEvent('page_hidden');
        this.endCurrentPageView();
      } else {
        this.trackEvent('page_visible');
      }
    });

    // Track before unload
    window.addEventListener('beforeunload', () => {
      this.trackEvent('session_end');
      this.endCurrentPageView();
      this.flushEvents();
    });
  }

  setUserId(userId: string) {
    this.userId = userId;
    this.trackEvent('user_identified', { userId });
  }

  trackEvent(event: string, properties: Record<string, any> = {}) {
    const analyticsEvent: AnalyticsEvent = {
      id: this.generateId(),
      event,
      properties: {
        ...properties,
        page: window.location.pathname,
        referrer: document.referrer
      },
      timestamp: new Date().toISOString(),
      userId: this.userId,
      sessionId: this.sessionId
    };

    this.events.unshift(analyticsEvent);
    
    // Keep events manageable
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(0, this.maxEvents);
    }

    // In development, log events
    if (import.meta.env.DEV) {
      console.log('[ANALYTICS]', event, properties);
    }

    // In production, batch and send to analytics service
    if (import.meta.env.PROD) {
      this.queueForBatch(analyticsEvent);
    }
  }

  trackPageView(page: string, properties: Record<string, any> = {}) {
    // End current page view
    this.endCurrentPageView();

    // Start new page view
    this.currentPage = {
      page,
      timestamp: new Date().toISOString(),
      userId: this.userId,
      sessionId: this.sessionId,
      referrer: document.referrer
    };

    this.pageViews.unshift(this.currentPage);
    
    this.trackEvent('page_view', {
      page,
      ...properties
    });
  }

  trackInteraction(element: string, action: string, properties: Record<string, any> = {}) {
    this.trackEvent('interaction', {
      element,
      action,
      ...properties
    });
  }

  trackError(error: string, context: Record<string, any> = {}) {
    this.trackEvent('error', {
      error,
      ...context
    });
  }

  trackPerformance(metric: string, value: number, unit: string = 'ms') {
    this.trackEvent('performance', {
      metric,
      value,
      unit
    });
  }

  private endCurrentPageView() {
    if (this.currentPage) {
      const duration = Date.now() - new Date(this.currentPage.timestamp).getTime();
      this.currentPage.duration = duration;
      
      this.trackEvent('page_view_end', {
        page: this.currentPage.page,
        duration
      });
    }
  }

  private queueForBatch(event: AnalyticsEvent) {
    // In a real app, you'd batch events and send to analytics service
    const batchKey = 'flamingo_analytics_batch';
    const batch = JSON.parse(localStorage.getItem(batchKey) || '[]');
    batch.push(event);
    
    // Keep batch size reasonable
    if (batch.length > 50) {
      batch.splice(0, batch.length - 50);
    }
    
    localStorage.setItem(batchKey, JSON.stringify(batch));
  }

  private flushEvents() {
    // Send any queued events before page unload
    const batchKey = 'flamingo_analytics_batch';
    const batch = JSON.parse(localStorage.getItem(batchKey) || '[]');
    
    if (batch.length > 0) {
      // Use sendBeacon for reliable delivery during page unload
      if (navigator.sendBeacon) {
        navigator.sendBeacon('/api/analytics', JSON.stringify(batch));
      }
      localStorage.removeItem(batchKey);
    }
  }

  cleanup() {
    // Clear old events and data
    const cutoff = Date.now() - (7 * 24 * 60 * 60 * 1000); // 7 days
    
    this.events = this.events.filter(event => 
      new Date(event.timestamp).getTime() > cutoff
    );
    
    this.pageViews = this.pageViews.filter(view => 
      new Date(view.timestamp).getTime() > cutoff
    );
  }

  getAnalytics() {
    const recentEvents = this.events.filter(event => 
      Date.now() - new Date(event.timestamp).getTime() < 24 * 60 * 60 * 1000 // Last 24 hours
    );

    const eventCounts = recentEvents.reduce((acc, event) => {
      acc[event.event] = (acc[event.event] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const recentPageViews = this.pageViews.filter(view => 
      Date.now() - new Date(view.timestamp).getTime() < 24 * 60 * 60 * 1000
    );

    const avgSessionDuration = recentPageViews
      .filter(view => view.duration)
      .reduce((sum, view) => sum + (view.duration! / 1000), 0) / recentPageViews.length;

    return {
      totalEvents: recentEvents.length,
      eventCounts,
      totalPageViews: recentPageViews.length,
      avgSessionDuration: Math.round(avgSessionDuration) || 0,
      topPages: this.getTopPages(recentPageViews)
    };
  }

  private getTopPages(pageViews: PageView[]) {
    const pageCounts = pageViews.reduce((acc, view) => {
      acc[view.page] = (acc[view.page] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(pageCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([page, views]) => ({ page, views }));
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

export const analyticsService = new AnalyticsService();

// Export trackEvent function for convenience
export const trackEvent = (event: string, properties?: Record<string, any>) => {
  analyticsService.trackEvent(event, properties);
};
